<script lang="ts">
							import SectionComponent from '$lib/section/SectionComponent.svelte';
							import copy from "copy-to-clipboard"; 
							import { onMount } from "svelte";
							import PrevNextSection from '$lib/prevNext/PrevNextSection.svelte';

							interface ComponentProps {
								setVisible: (id: string, v: boolean) => void;
							}
							let { setVisible }: ComponentProps = $props();
							
							const visibleById = $state<Record<string, boolean>>({});

							/**
               * This function will go through all the 'pre' elements
               * on the page and add a copy button to them.
               * Thanks to: https://slavbasharov.com/blog/adding-click-to-copy-code-markdown-blog
               */
              onMount(() => {
                  const codeBlocks = document.querySelectorAll("pre");
                  codeBlocks.forEach((block) => {
                      const copyPrompt = document.createElement("div");
                      copyPrompt.className = "copy-prompt";
                      const copyPromptText = document.createElement("p");
                      copyPromptText.innerHTML = "👆 Click to copy";
                      copyPromptText.className = 'copy-prompt-p';
                      const copyIcon = document.createElement("img");
                      copyIcon.src = "/icons/copy-icon.svg";
                      copyIcon.className = "copy-prompt-img";
                      copyPrompt.appendChild(copyIcon);
                      copyPrompt.appendChild(copyPromptText);
                      block.appendChild(copyPrompt);
											block.querySelector('.copy-prompt > p')?.addEventListener('click', () => {
												if (block.querySelector('code')?.textContent !== null && block.querySelector('code')?.textContent !== undefined) {
													copy(block.querySelector('code')!.textContent!);
												}
												if (block.querySelector('.copy-prompt > p')?.innerHTML !== null && block.querySelector('.copy-prompt > p')?.innerHTML !== undefined) {
													block.querySelector('.copy-prompt > p')!.innerHTML = 'Copied!';
													setTimeout(() => {
														block.querySelector('.copy-prompt > p')!.innerHTML = '👆 Click to copy';
													}, 1000);
												}
											});
                  });
              });
		let prevLink= '/Documentation/Defining_Scope_Rules/Basics_and_Terminology';
    let nextLink= '/Documentation/Defining_Scope_Rules/Namespace_Alternatives';
    
    import Figure from "$lib/figures/Figure.svelte";
</script>

<PrevNextSection {prevLink} {nextLink} />



<SectionComponent tag="h1" 
  id="namespace-imports-1"
  bind:intersecting={visibleById["namespace-imports-1"]}
  onIntersectChange={(visible) => setVisible("namespace-imports-1", visible)}
>Namespace Imports</SectionComponent>
<p>Previously, we saw that the set of visible nodes in a namespace consists of the nodes it declares and
the nodes visible in its parent namespace. This standard visibility can be extended with <strong>imports</strong>.
When a namespace is imported into
another namespace, its declared nodes are added to the visible nodes of the latter.</p>
<p>In the following example, <code>basedOn</code> is in the language structure definition (<code>.ast</code> file) a property of the
<code>InsuranceProduct</code> concept. </p>
<pre class="language-freon"><code class="language-freon">// Insurance/src/defs/language-main.ast#L53-L65

concept InsuranceProduct &#123;
    name: identifier;                       // internal name
    productName: string;                    // name by which this product is known to the public
    themes: InsuranceTheme[];               // the 'kind' of insurance
    advertisedPremium: EuroLiteral;         // the premium as known to the public
    nrPremiumDays: PremiumDays;             // the number of days for which the advertised premium is calculated
    reference parts: InsurancePart[];       // optionally, known parts can be included by reference
    reference basedOn: BaseProduct[];       // the BaseProducts from which the parts are taken

    riskAdjustment?: PercentageLiteral;     // an adjustment to the risk of the separate parts, e.g. caused by the combination of the parts
    calculation: DocuExpression;            // the premium as calculated based on the parts
    helpers: CalcFunction[];                // helper functions used to calculate the premium
&#125;</code></pre>
<p>If the following import statement is included in the .scope file, the declared nodes in the <code>basedOn</code> property in the user’s model
will be included in the namespace identified by an <code>InsuranceProduct</code>. Note that both <code>InsuranceProduct</code> and
<code>BaseProduct</code> (the type of <code>basedOn</code>) need to be defined as a namespace.</p>
<pre class="language-freon"><code class="language-freon">// Insurance/src/defs/scoper-docu.scope#L3-L9

isNamespace &#123; InsuranceProduct, BaseProduct, CalcFunction, Entity, AttributeRef &#125;

InsuranceProduct &#123;
    imports &#123;
        self.basedOn;
    &#125; 
&#125;</code></pre>
<SectionComponent tag="h2" 
  id="effect-on-the-namespace-tree-2"
  bind:intersecting={visibleById["effect-on-the-namespace-tree-2"]}
  onIntersectChange={(visible) => setVisible("effect-on-the-namespace-tree-2", visible)}
>Effect on the Namespace Tree</SectionComponent>
<p>A namespace import effectively turns the namespace tree into a graph, like references make the abstract syntax <em>tree</em> become a graph.
For instance, when the namespace A8 is imported into namespace A3 an extra edge is made between nodes A3 and A8, as shown in
the next figure. This edge signifies the inclusion of the declared nodes of A8 ([F1, D7, D8]) in the visible nodes of A3.</p>
<Figure
imageName={'documentation/AST-plus-reference.png'}
caption={'Graph showing an import between Namespace nodes'}
figureNumber={1}
/>
<SectionComponent tag="h2" 
  id="example-defining-inheritance-3"
  bind:intersecting={visibleById["example-defining-inheritance-3"]}
  onIntersectChange={(visible) => setVisible("example-defining-inheritance-3", visible)}
>Example: Defining Inheritance</SectionComponent>
<p>Using namespace imports, you can support some kind of inheritance in your DSL. Suppose <code>baseClass</code> represents the “super class”
of <code>ClassWithInheritance</code> in your language, as follows.</p>
<pre class="language-freon"><code class="language-freon">/* The .ast file */

concept ClassWithInheritance &#123;
    name: identifier;
    reference baseClass?: ClassWithInheritance;
    methods: Method[];
&#125;

concept Method &#123;
    name: identifier;
    parameters: Parameter[];
    body: Body;
&#125;</code></pre>
<p>For brevity, the definitions of <code>Parameter</code> and <code>Body</code> are omitted. The only thing you need to know is
that <code>Body</code> is an expression in which you can refer to a method.</p>
<p>Next, use a scope import to include all the declared nodes of the “super class” in the visible nodes of the “subclass”.</p>
<pre class="language-freon"><code class="language-freon">/* The .scope file */

ClassWithInheritance &#123;
    imports &#123;
        self.baseClass;
    &#125; 
&#125;
</code></pre>
<p>Now suppose, the user’s model is this. (Again, for brevity, we do not include the editor definition,
but the keywords <code>class</code>, <code>super</code>, and <code>method</code> are a definite giveaway to the link between concrete
and abstract syntax.😊)</p>
<pre class=""><code class="">class SmallBusiness 
  method calculateTurnover() &#123;
    // some expression
  &#125;
  method calculateRevenue() &#123;
    // some expression
  &#125;
  method calculateValueAddedTax() &#123;
    // some expression
  &#125;
  
class DryCleaner super SmallBusiness
  method revenueAsPercentageOfTurnover() &#123;
    // expression using references to calculateRevenue and calculateTurnover
  &#125;  
</code></pre>
<p>The user can reference the methods of <code>SmallBusiness</code> because they are
in the declared nodes of the namespace identified by <code>SmallBusiness</code>, and they are imported in the
<code>DryCleaner</code> namespace by the import statement in the scope file.</p>
<SectionComponent tag="h2" 
  id="recursive-imports-4"
  bind:intersecting={visibleById["recursive-imports-4"]}
  onIntersectChange={(visible) => setVisible("recursive-imports-4", visible)}
>Recursive Imports</SectionComponent>
<p>Sometimes you may want to include not only the declared nodes of the imported namespace, but its imported
nodes as well. In the example above, where a sort of inheritance is defined, you would want the
methods of the superclass of the superclass also to be included. We can define this in the scope
file by simply adding the keyword <code>recursive</code> in front of the import. Each import in the list can
have its own keyword, in this way you have a fine-grained control over what is added.</p>
<pre class="language-freon"><code class="language-freon">ClassWithInheritance &#123;
    imports &#123;
        recursive self.baseClass;
        self.someOtherProperty;
    &#125; 
&#125;
</code></pre>
<SectionComponent tag="h2" 
  id="public-and-private-properties-5"
  bind:intersecting={visibleById["public-and-private-properties-5"]}
  onIntersectChange={(visible) => setVisible("public-and-private-properties-5", visible)}
>Public and Private Properties</SectionComponent>
<p>To add an even finer control over what and what is not imported, you can add the keyword ‘private’
to the definition of properties in the .ast file. (Not in the .scope file!) Any property that is
thus marked <code>private</code> will not be included in the set of imported nodes. The marking does not affect
the declared nodes, only the imported nodes of any namespace that imports a namespace with private
properties. This means that properties marked <code>private</code> are visible within their own namespace,
but not when that namespace is imported elsewhere.</p>
<p>Suppose that in the inheritance example we want to have private methods. We could adjust the .ast file like this:</p>
<pre class="language-freon"><code class="language-freon">concept ClassWithInheritance &#123;
    name: identifier;
    reference baseClass?: ClassWithInheritance;
    methods: Method[];
    private privateMethods: Method[];
&#125;</code></pre>
<p>Suppose further that the user model is the following:</p>
<pre class=""><code class="">class SmallBusiness 
  method calculateTurnover() &#123;
    // some expression, where internalCalculation may be referenced
  &#125;
  method calculateRevenue() &#123;
    // some expression, where internalCalculation may be referenced
  &#125;
  method calculateValueAddedTax() &#123;
    // some expression, where internalCalculation may be referenced
  &#125;
  private method internalCalculation() &#123;
    // some expression, where internalCalculation may be referenced
  &#125;

  
class DryCleaner super SmallBusiness
  method revenueAsPercentageOfTurnover() &#123;
    // expression using references to calculateRevenue, calculateTurnover, as well as someOtherCalculation, but NOT to internalCalculation
  &#125;  
  private method someOtherCalculation() &#123;
    // expression using references to calculateRevenue, calculateTurnover, as well as someOtherCalculation, but NOT to internalCalculation 
  &#125;</code></pre>
<p>Now the visible nodes of <code>SmallBusiness</code> would include <code>internalCalculation</code>, whereas the visible nodes of
<code>DryCleaner</code> would not. It would, however, include <code>someOtherCalculation</code>. But, if <code>DryCleaner</code> was
imported in another namespace, <code>someOtherCalculation</code> would not be part of that namespace.</p>
<p>The next page will explain how to leave out the standard hierarchical namespaces (lexical scope)
using <a
  href="/Documentation/Defining_Scope_Rules/Namespace_Alternatives"
>namespace alternatives</a>.</p>


<PrevNextSection {prevLink} {nextLink} />